import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Touch-keyboard Return key behavior:
 * `enterKeyHint` is a standard HTML attribute that tells iOS/Android
 * which native action label to render on the keyboard's Return key.
 *
 * We default to `"done"` for standard text-like inputs. iOS/Android
 * automatically translate this label to the user's device language
 * ("Done" in English, "Hecho" in Spanish, "Fine" in Italian, etc.)
 * and render it as the blue confirm button.
 *
 * IMPORTANT: `enterKeyHint` only changes the LABEL — it does not wire
 * any behavior. To actually dismiss the keyboard when the user taps it,
 * we attach an `onKeyDown` handler below that blurs the input on Enter
 * (which collapses the on-screen keyboard on iOS Safari and Android
 * Chrome). If the input is inside a `<form>`, we also call
 * `form.requestSubmit()` so existing submit handlers keep firing.
 */
function defaultEnterKeyHint(
  type: React.HTMLInputTypeAttribute | undefined,
): React.HTMLAttributes<HTMLInputElement>["enterKeyHint"] | undefined {
  switch (type) {
    case "search":
      return "search";
    case "password":
      return "go";
    case "email":
    case "tel":
    case "url":
    case "text":
    case undefined:
      return "done";
    default:
      // number, date, file, checkbox, radio, etc. — let the OS decide.
      return undefined;
  }
}

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, enterKeyHint, inputMode, onKeyDown, ...props }, ref) => {
    const resolvedHint = enterKeyHint ?? defaultEnterKeyHint(type);
    // Default to the decimal numeric keyboard for number inputs on touch
    // devices, so users don't get the full alphanumeric keyboard.
    const resolvedInputMode =
      inputMode ?? (type === "number" ? "decimal" : undefined);

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Always run caller-provided handler first so overrides win.
        onKeyDown?.(e);
        if (e.defaultPrevented) return;

        // Only react to a plain Enter press.
        if (e.key !== "Enter") return;
        if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;

        // Let search inputs behave natively (they often trigger search submit).
        if (type === "search") return;

        const el = e.currentTarget;
        const form = el.form;
        if (form) {
          // Trigger native form submission so any onSubmit runs.
          e.preventDefault();
          form.requestSubmit();
        }
        // Blur the input — this is what actually closes the
        // on-screen keyboard on iOS and Android. Belt-and-braces:
        // also blur on the next animation frame in case focus was
        // restored synchronously by the form submit handler.
        el.blur();
        if (typeof window !== "undefined") {
          window.requestAnimationFrame(() => {
            try {
              el.blur();
            } catch {
              /* ignore */
            }
          });
        }
      },
      [onKeyDown, type],
    );

    return (
      <input
        type={type}
        enterKeyHint={resolvedHint}
        inputMode={resolvedInputMode}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-11 w-full rounded-xl border px-4 py-2 text-base ring-offset-background file:border-0 file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-[#2B7DE0] disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 md:text-sm mt-1 bg-white border-[#b6dded] shadow-sm",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
